import React from 'react';
import { PopupRenderer } from './PopupProvider/PopupRenderer';

import {
  closePopup,
  openPopup,
  resetPopup,
  setPopupProps,
  updatePopupProps,
} from '../store/popup/popupReducer';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { getPopupState } from '../store/popup/popupStateSelector';

export interface PopupContext {
  openPopup: () => void;
  closePopup: () => void;
  setPopupProps: (componentName: string, componentProps: unknown) => void;
  updatePopupProps: (componentProps: unknown) => void;
  resetPopup: () => void;
}

export const popupContext = React.createContext<PopupContext>({
  openPopup: Function,
  setPopupProps: Function,
  resetPopup: Function,
  closePopup: Function,
  updatePopupProps: Function,
});

export function usePopup(
  componentName: string,
  props: Record<string, unknown>
) {
  const context = React.useContext(popupContext);

  const openPopup = React.useCallback(() => {
    context.setPopupProps(componentName, props);
    context.openPopup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    return () => {
      context.resetPopup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { ...context, openPopup };
}

export function PopupProvider({ children }: React.PropsWithChildren<unknown>) {
  const dispatch = useAppDispatch();

  const state = useAppSelector(getPopupState);

  const dispatchOpenPopup = React.useCallback(
    () => dispatch(openPopup()),
    [dispatch]
  );

  const dispatchClosePopup = React.useCallback(
    () => dispatch(closePopup()),
    [dispatch]
  );

  const dispatchResetPopup = React.useCallback(
    () => dispatch(resetPopup()),
    [dispatch]
  );

  const dispatchSetComponentProps = React.useCallback(
    (componentName: string, componentProps: Record<string, unknown>) =>
      dispatch(setPopupProps({ componentName, componentProps })),
    [dispatch]
  );

  const dispatchUpdateComponentProps = React.useCallback(
    (componentProps: Record<string, unknown>) =>
      dispatch(updatePopupProps(componentProps)),
    [dispatch]
  );

  return (
    <popupContext.Provider
      value={{
        openPopup: dispatchOpenPopup,
        closePopup: dispatchClosePopup,
        setPopupProps: dispatchSetComponentProps,
        updatePopupProps: dispatchUpdateComponentProps,
        resetPopup: dispatchResetPopup,
      }}
    >
      {children}
      <PopupRenderer {...state} />
    </popupContext.Provider>
  );
}
